// Nearby builds — the roofs Bardin built (or has on the build calendar) near
// a stop. Powers the Today / My Stops Today "Nearby builds" button on the
// Dispatch Board (Oct 22, 2026): a roof specialist opens it at an adjuster
// meeting to show the adjuster the roofs we've replaced within a few miles.
//
// Data comes from the definer RPC builds_near_point (deals RLS would hide
// other reps' jobs from an RS). It returns no homeowner names and no money on
// purpose: the screen is shown to a third party. Change the RPC and this file
// together.
import { format, subMonths } from "date-fns";
import { supabase } from "./supabase";

export type NearbyBuildStatus = "built" | "scheduled";

export interface NearbyBuild {
  deal_id: string;
  street: string | null;
  city: string | null;
  zip: string | null;
  lat: number;
  lon: number;
  status: NearbyBuildStatus;
  /** yyyy-mm-dd — the build completion date (built rows). */
  built_on: string | null;
  /** yyyy-mm-dd — the calendar date (scheduled rows; also set on built rows). */
  scheduled_for: string | null;
  carrier: string | null;
  is_retail: boolean;
  distance_m: number;
}

export const METERS_PER_MILE = 1609.344;

export const RADIUS_OPTIONS_MI = [2, 5, 10] as const;
export type RadiusMi = (typeof RADIUS_OPTIONS_MI)[number];
export const DEFAULT_RADIUS_MI: RadiusMi = 5;

/** One fetch at the widest radius; the radius chips filter in the browser. */
export const FETCH_RADIUS_MI = 10;

export type PeriodKey = "12m" | "24m" | "all";
export const PERIOD_OPTIONS: { key: PeriodKey; label: string; months: number | null }[] = [
  { key: "all", label: "All time", months: null },
  { key: "24m", label: "Last 2 years", months: 24 },
  { key: "12m", label: "Last 12 months", months: 12 },
];

export function periodMonths(key: PeriodKey): number | null {
  return PERIOD_OPTIONS.find((p) => p.key === key)?.months ?? null;
}

/** yyyy-mm-dd of `months` ago, in the browser's local calendar. */
export function periodCutoffISO(months: number, now: Date = new Date()): string {
  return format(subMonths(now, months), "yyyy-MM-dd");
}

/**
 * The rows inside `radiusMi`, and (when a period is set) the built rows whose
 * completion date is inside it. Scheduled builds always show: they are on the
 * calendar now, whatever the period.
 */
export function filterNearby(
  rows: NearbyBuild[],
  radiusMi: number,
  months: number | null,
  now: Date = new Date(),
): NearbyBuild[] {
  const maxM = radiusMi * METERS_PER_MILE;
  const cutoff = months == null ? null : periodCutoffISO(months, now);
  return rows.filter((r) => {
    if (!(r.distance_m <= maxM)) return false;
    if (cutoff == null || r.status === "scheduled") return true;
    return !!r.built_on && r.built_on >= cutoff;
  });
}

export interface NearbySummary {
  built: number;
  scheduled: number;
  retail: number;
  /** Insurance builds by carrier, biggest first. Retail jobs and blank carriers are left out. */
  carriers: { name: string; count: number }[];
  /** Newest completion date (yyyy-mm-dd) among the built rows. */
  newestBuilt: string | null;
}

export function summarizeNearby(rows: NearbyBuild[]): NearbySummary {
  let built = 0;
  let scheduled = 0;
  let retail = 0;
  let newestBuilt: string | null = null;
  const byCarrier = new Map<string, { name: string; count: number }>();
  for (const r of rows) {
    if (r.status === "built") {
      built += 1;
      if (r.built_on && (!newestBuilt || r.built_on > newestBuilt)) newestBuilt = r.built_on;
    } else {
      scheduled += 1;
    }
    if (r.is_retail) {
      retail += 1;
      continue;
    }
    if (r.status !== "built") continue;
    const name = (r.carrier ?? "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const hit = byCarrier.get(key);
    if (hit) hit.count += 1;
    else byCarrier.set(key, { name, count: 1 });
  }
  const carriers = [...byCarrier.values()].sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );
  return { built, scheduled, retail, carriers, newestBuilt };
}

/** "12 roofs built within 5 mi · 2 more scheduled" */
export function nearbyHeadline(summary: NearbySummary, radiusMi: number): string {
  const roofs = summary.built === 1 ? "1 roof built" : `${summary.built} roofs built`;
  const base = `${roofs} within ${radiusMi} mi`;
  if (summary.scheduled === 0) return base;
  return `${base} · ${summary.scheduled} more scheduled`;
}

/** "0.3 mi", "4.8 mi". Anything under a tenth reads as 0.1. */
export function formatMiles(meters: number): string {
  const mi = meters / METERS_PER_MILE;
  const rounded = Math.max(0.1, Math.round(mi * 10) / 10);
  return `${rounded.toFixed(1)} mi`;
}

/** "Mar 4, 2026" from a yyyy-mm-dd string, without the date-only UTC trap. */
export function formatBuildDate(iso: string | null | undefined): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return "";
  return format(new Date(iso.slice(0, 10) + "T12:00:00"), "MMM d, yyyy");
}

/** One line for a row: "Built Mar 4, 2026" / "Scheduled for Nov 2, 2026". */
export function statusLine(r: Pick<NearbyBuild, "status" | "built_on" | "scheduled_for">): string {
  if (r.status === "built") {
    const d = formatBuildDate(r.built_on);
    return d ? `Built ${d}` : "Built";
  }
  const d = formatBuildDate(r.scheduled_for);
  return d ? `Scheduled for ${d}` : "Scheduled";
}

/** Polygon ring approximating a circle of `meters` around [lon, lat] (GeoJSON order). */
export function circlePolygon(center: [number, number], meters: number, steps = 72) {
  const [lon, lat] = center;
  const dLat = meters / 111320;
  const dLon = meters / (111320 * Math.cos((lat * Math.PI) / 180));
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i * 2 * Math.PI) / steps;
    ring.push([lon + Math.cos(t) * dLon, lat + Math.sin(t) * dLat]);
  }
  return {
    type: "Feature" as const,
    properties: {},
    geometry: { type: "Polygon" as const, coordinates: [ring] },
  };
}

function num(v: unknown): number {
  if (v == null || v === "") return NaN; // Number(null) is 0, which would put a roof on the equator
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/** Normalise one RPC row: numerics may arrive as strings, dates as yyyy-mm-dd. */
export function coerceNearbyBuild(raw: Record<string, unknown>): NearbyBuild | null {
  const lat = num(raw.lat);
  const lon = num(raw.lon);
  const distance_m = num(raw.distance_m);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || !Number.isFinite(distance_m)) return null;
  const status: NearbyBuildStatus = raw.status === "scheduled" ? "scheduled" : "built";
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);
  return {
    deal_id: String(raw.deal_id ?? ""),
    street: str(raw.street),
    city: str(raw.city),
    zip: str(raw.zip),
    lat,
    lon,
    status,
    built_on: str(raw.built_on)?.slice(0, 10) ?? null,
    scheduled_for: str(raw.scheduled_for)?.slice(0, 10) ?? null,
    carrier: str(raw.carrier),
    is_retail: raw.is_retail === true,
    distance_m,
  };
}

/**
 * Roofs built or on the calendar within `radiusMi` of a point, nearest first.
 * Throws on a failed read — a map that quietly shows nothing would look like
 * "we've built nothing here".
 */
export async function fetchNearbyBuilds(opts: {
  lat: number;
  lon: number;
  radiusMi?: number;
  excludeDealId?: string | null;
}): Promise<NearbyBuild[]> {
  const radiusMi = opts.radiusMi ?? FETCH_RADIUS_MI;
  const { data, error } = await supabase.rpc("builds_near_point", {
    p_lat: opts.lat,
    p_lon: opts.lon,
    p_radius_m: radiusMi * METERS_PER_MILE,
    p_exclude_deal_id: opts.excludeDealId ?? null,
  });
  if (error) throw new Error(error.message);
  const list = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
  return list
    .map(coerceNearbyBuild)
    .filter((r): r is NearbyBuild => r !== null)
    .sort((a, b) => a.distance_m - b.distance_m);
}
